import assertNever from '@watershed/shared-util/src/assertNever';

import { assertNumber, debugAssert } from './assert';
import type { ASTExpr } from './ast';
import { Quantity } from './quantity';
import type { Context } from './types';

/*
 * Eval an expression that can only do math, it can't do any function calls. This lets it be used in the
 * context of a synchronous unit conversion.
 */

export function evalLimitedExpression(ctx: Context, expr: ASTExpr): unknown {
  switch (expr.t) {
    case 'number':
      return expr.v;
    case 'unary':
      switch (expr.op) {
        case '-': {
          const v = evalLimitedExpression(ctx, expr.expr);
          if (typeof v === 'number') {
            return -v;
          } else if (v instanceof Quantity) {
            return new Quantity(-v, v.unit);
          } else {
            throw new Error(
              `error: unknown type ${v} for unary minus. Expected Quantity or number`
            );
          }
        }
        default:
          assertNever(expr);
      }
    case 'identifier': {
      const v = ctx.variables.get(expr.name);
      debugAssert(
        v !== undefined,
        () =>
          `unknown variable ${expr.name} (know about ${[
            ...ctx.variables.keys(),
          ].join(', ')})`
      );
      return v;
    }
    case 'binary': {
      const [lEval, rEval] = [
        evalLimitedExpression(ctx, expr.l),
        evalLimitedExpression(ctx, expr.r),
      ];
      const l = assertNumber(lEval);
      const r = assertNumber(rEval);
      switch (expr.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          if (r === 0) {
            throw new Error(`error: cannot divide by zero in expression`);
          }
          return l / r;
        default:
          assertNever(expr);
      }
    }
    case 'comparison':
      throw new Error('Cannot do comparisons in limited expressions');
    case 'call':
      throw new Error('Cannot do function calls in limited expressions');
    case 'string':
      throw new Error("Can't use strings in limited expressions");
    case 'boolean':
      throw new Error("Can't use booleans in limited expressions");
    case 'null':
      throw new Error("Can't use null in limited expressions");
    default:
      assertNever(expr);
  }
}
